import { DOCUMENT } from "@angular/common";
import { Directive, ElementRef, HostListener, Inject, Input, OnChanges, OnInit, Renderer2 } from "@angular/core";
import { NgControl, ValidationErrors } from "@angular/forms";
import { BehaviorSubject, Subscription } from "rxjs";

/**
 * Appends a <small> tag below a form control.
 */
@Directive({
    selector: "[kno2FormControl]",
    standalone: false
})
export class FormControlDirective implements OnInit, OnChanges {
    /**
     * Error message overrides provided as a ValidationErrors object.
     *
     * E.g., `[kno2FormControlError]="{ maxlength: 'Custom message' }"`
     */
    @Input() public errors?: ValidationErrors;
    @Input() public containerId?: string;
    @Input() public showErrorOn?: "touched" | "dirty" | "never";
    private blur$ = new BehaviorSubject(false);
    private messageContainer: any;
    private messageContainerText: any;
    private message: string;
    private subscriptions: Array<Subscription | undefined> = [];

    constructor(
        @Inject(DOCUMENT) private document: Document,
        private control: NgControl,
        private element: ElementRef,
        private renderer: Renderer2
    ) {}

    @HostListener("focusout")
    public onFocusOut(): void {
        this.blur$.next(true);
    }

    public ngOnInit(): void {
        const control = this.control;

        this.subscriptions.push(
            this.blur$.subscribe(() => {
                this.initiateErrorHandling(control, control.status);
                control.control.updateValueAndValidity();
            }),
            control.statusChanges.subscribe((controlStatus) => {
                this.initiateErrorHandling(control, controlStatus);
            }),
            control.valueChanges.subscribe(() => {
                this.initiateErrorHandling(control, control.status);
            })
        );
    }

    public ngOnChanges(): void {
        this.initiateErrorHandling(this.control, this.control.status);
    }

    private initiateErrorHandling(control: NgControl, controlStatus: string): void {
        if (this.messageContainer) this.removeMessageContainer();

        if (control.status === "VALID") return;

        if (this.showErrorOn === "dirty" && !control.dirty) return;

        if (this.showErrorOn === "touched" && !control.touched) return;

        this.handleValidationMessage(controlStatus);
    }

    private handleValidationMessage(status: string) {
        this.configureMessageContainer();

        if (this.messageContainerText) this.removeMessageText();

        if (status === "INVALID" && this.control.errors) {
            const firstError = Object.keys(this.control.errors)[0];
            this.message = this.errors?.[firstError] ?? this.getErrorMessage(this.control.errors);
        }

        if (this.message) this.addMessageText();
    }

    private getErrorMessage(errors: ValidationErrors): string {
        const key = Object.keys(errors)[0];
        const messages: ValidationErrors = {
            required: "This field is required",
            maxlength: `Maximum length is ${errors.maxlength?.requiredLength}`
        };

        return messages[key] || "Invalid value";
    }

    /**
     * Prepares the message container. Call addMessageText() to display the message.
     */
    private configureMessageContainer(): void {
        this.messageContainer = this.renderer.createElement("small");
        this.renderer.addClass(this.messageContainer, "kno2-form-control");

        this.renderer.appendChild(this.getElementContainer(), this.messageContainer);
    }

    /**
     * Adds the text to the message container. Text is not visible until this method is called.
     *
     * @param status - The .kno2-form-control sub-class to determine the text color.
     */
    private addMessageText(status: string = "error") {
        this.messageContainerText = this.renderer.createText(this.message);
        this.renderer.addClass(this.messageContainer, status);
        this.renderer.appendChild(this.messageContainer, this.messageContainerText);
    }

    /**
     * Removes the text from the message container / hides the message.
     */
    private removeMessageText(): void {
        this.renderer.removeChild(this.messageContainer, this.messageContainerText);
    }

    private removeMessageContainer(): void {
        this.renderer.removeChild(this.getElementContainer(), this.messageContainer);
    }

    private getElementContainer() {
        return this.containerId ? this.document.getElementById(this.containerId) : this.element.nativeElement.parentNode;
    }
}
